import type { GetLabels200Item } from '@ipfabric/api-client/client/schemas';
import { useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useNotifications } from 'utils/hooks/useNotifications';
import type { DataPolicyFormValues } from './dataPolicyFormSchema';

type UsePruneUnenrolledTermsParams = {
  form: UseFormReturn<DataPolicyFormValues>;
  enrolledLabels: readonly GetLabels200Item[];
  labelsLoaded: boolean;
  enabled: boolean;
};

/**
 * Conditions may only use enrolled labels; the backend rejects anything else in
 * preview and save. When a label stops being enrolled (e.g. un-enrolled from the
 * labels sub-view, or by someone else), drop its rows and say so.
 */
export const usePruneUnenrolledTerms = ({
  form,
  enrolledLabels,
  labelsLoaded,
  enabled,
}: UsePruneUnenrolledTermsParams) => {
  const { showNotification } = useNotifications();
  const enrolledKey = enrolledLabels
    .map((label) => label.name)
    .sort()
    .join('\n');

  useEffect(() => {
    if (!(enabled && labelsLoaded)) {
      return;
    }
    const enrolled = new Set(enrolledKey.split('\n'));
    const terms = form.getValues('terms');
    const removed = terms.filter((term) => !enrolled.has(term.label));
    if (removed.length === 0) {
      return;
    }
    form.setValue(
      'terms',
      terms.filter((term) => enrolled.has(term.label)),
      { shouldDirty: true, shouldValidate: true },
    );
    showNotification({
      type: 'warning',
      message: `Removed conditions on labels that are no longer enrolled: ${removed
        .map((term) => term.label)
        .join(', ')}`,
    });
  }, [enabled, labelsLoaded, enrolledKey, form]);
};
